import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { getRouter } from '@/router'
import axios from 'axios'
import App from './App.vue'
import vuetify from '@/plugins/vuetify'
import { loadFonts } from '@/plugins/webfontloader'
import { LOGGER } from '@/util/logger'
import { useConfigStore } from '@/stores/config'
import { useUserLogin } from '@/stores/userLogin'
import { IDENTITY_PROVIDER_SERVICE_ENDPOINT } from '@/constants/Endpoints'

// Create app
const app = createApp(App)
app.use(createPinia())
const configStore = useConfigStore()
const userLoginStore = useUserLogin()
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error)
    } else {
      promise.resolve(token)
    }
  })

  failedQueue = []
}

const refreshAccessToken = async () => {
  const response = await fetch(IDENTITY_PROVIDER_SERVICE_ENDPOINT.REFRESH_TOKEN, {
    method: 'POST',
    headers: {
      'Ocp-Apim-Subscription-Key': import.meta.env.VITE_APP_OCP_APIM_SUBSCRIPTION_KEY,
    },
    body: JSON.stringify(userLoginStore.getSessionRefreshPayload()),
  })
  const { accessToken } = await response.json()
  userLoginStore.currentToken = accessToken
  return accessToken
}

// Configure axios interceptors
const setupAxios = () => {
  axios.interceptors.request.use(async (config) => {
    try {
      config.headers['Ocp-Apim-Subscription-Key'] = import.meta.env.VITE_APP_OCP_APIM_SUBSCRIPTION_KEY
      const currentToken = userLoginStore.currentToken
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`
        if (import.meta.env.VITE_NODE_ENV == 'development') {
          const { jwtDecode } = await import('jwt-decode')
          //Temporary code - The egov-keys are set from APIM after parsing the JWT
          const decoded = jwtDecode(currentToken)
          config.headers['keys'] = decoded.keys
          config.headers['transactionID'] = decoded['transaction-id']
        }
      }
      return config
    } catch (error) {
      LOGGER.logException(error)
      throw error
    }
  })

  // Response interceptor
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              return axios(originalRequest)
            })
            .catch((err) => {
              throw err
            })
        }
        originalRequest._retry = true
        isRefreshing = true
        try {
          const newAccessToken = await refreshAccessToken()
          processQueue(null, newAccessToken)
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
          return axios(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError, null)
          // Logout user if refresh token is invalid
          userLoginStore.logout?.()
          throw refreshError
        } finally {
          isRefreshing = false
        }
      }
      throw error
    },
  )
}

// Main initialization function
function initializeApp() {
  try {
    const router = getRouter()
    // Load fonts
    loadFonts()

    // Setup axios first
    setupAxios()

    // Mount the app
    app.use(router)
    app.use(vuetify)
    app.mount('#app')

    // Load reference data into config store
    configStore.loadReferenceData(true)
    //Load DVS center phone, tty and hours
    configStore.loadDVSCenterInfo()
    //load envConfig properties that used across the public app
    configStore.loadEnvironmentConfigs()
  } catch (error) {
    LOGGER.logException(error, true)
  }
}

// Start the application
initializeApp()
